import { useLayoutEffect, useRef, useState } from 'react';

import { ReactComponent as LogomarkInk } from '@rocco/icons/svg/logo-new-black.svg';
import { ReactComponent as LogomarkWhite } from '@rocco/icons/svg/logo-new-white.svg';

const MOBILE_QUERY = '(max-width: 767px)';

/** How far the mark is blurred once it has fully faded. */
const MAX_BLUR_PX = 20;

/**
 * The directory's A mark below md: pinned to the screen where the page
 * first drew it, the content scrolling over it, fading and blurring as it
 * goes and gone the moment the box it left in the page reaches the underside
 * of the fixed header — so it dissolves into the page rather than sliding
 * behind the bar.
 *
 * The box keeps the mark's place in the layout; the drawing itself is
 * fixed at that place on screen, one layer under the column's content (the
 * column isolates its stacking for it) and over the page's ground. The
 * distance is read against the header as it stands, so a warning banner
 * that makes the header taller brings the vanishing point down with it. A
 * reader who asks for less motion gets the mark scrolling away with the page
 * instead.
 */
export const FadingLogomark = ({
    tone = 'light',
}: {
    /** The ground it stands on: the projects directory is dark. */
    tone?: 'light' | 'dark';
} = {}) => {
    const Logomark = tone === 'dark' ? LogomarkWhite : LogomarkInk;
    const boxRef = useRef<HTMLDivElement>(null);
    const markRef = useRef<HTMLDivElement>(null);
    // Bumped when the breakpoint or the motion preference moves, so the effect
    // reconsiders whether there is anything to listen for.
    const [reattach, setReattach] = useState(0);

    // Layout, not effect: the mark is placed by writing `position: fixed` onto
    // it, so on an effect it drew once in the flow of the page before jumping
    // to where it belongs — a visible hop on every load.
    useLayoutEffect(() => {
        const mobile = window.matchMedia(MOBILE_QUERY);
        const calm = window.matchMedia('(prefers-reduced-motion: reduce)');
        const onChange = () => setReattach((count) => count + 1);
        const apply = () => {
            const box = boxRef.current;
            const mark = markRef.current;
            if (!box || !mark) return;
            if (!mobile.matches || calm.matches) {
                mark.style.position = '';
                mark.style.top = '';
                mark.style.left = '';
                mark.style.opacity = '';
                mark.style.filter = '';
                mark.style.visibility = '';
                return;
            }
            const header = document.getElementById('mobile-header');
            const headerBottom = header?.getBoundingClientRect().bottom ?? 0;
            // How far the box still is from the header, against how far it
            // started: 1 at rest, 0 on contact.
            const rect = box.getBoundingClientRect();
            const distance = rect.top - headerBottom;
            const start = rect.top + window.scrollY - headerBottom;
            const progress =
                start > 0 ? Math.min(Math.max(distance / start, 0), 1) : 1;
            // Pinned where the box sits with the page at its top.
            mark.style.position = 'fixed';
            mark.style.top = `${rect.top + window.scrollY}px`;
            mark.style.left = `${rect.left}px`;
            mark.style.opacity = String(progress);
            mark.style.filter = `blur(${(1 - progress) * MAX_BLUR_PX}px)`;
            // Gone for good once faded: a blurred ghost at zero opacity
            // still costs a paint.
            mark.style.visibility = progress > 0 ? '' : 'hidden';
            // Only while it is actually moving. Left on, it holds a composited
            // layer for the life of the page — and the mark spends most of
             // that time settled at one end or the other.
            mark.style.willChange =
                progress > 0 && progress < 1 ? 'opacity, filter' : '';
        };

        // Coalesced to one run per frame. `apply` takes two measurements and
        // then writes, so straight off the scroll event it forced a layout and
        // a blurred repaint for every event the browser chose to fire —
        // several per frame on a phone, on the first screen of all three
        // directory landings. (The note this replaces worried about a frame
        // never arriving in a hidden tab; a hidden tab does not scroll.)
        //
        // The trailing run is the point of `again`. Dropping events while a
        // frame is pending loses the last one of a gesture, and the last one
        // is the only one that reports where the reader actually stopped — so
        // the mark kept whatever position the second-to-last frame measured.
        // Coming back to the top of /projects that left it 69px low, pinned
        // mid-page, for the life of the page.
        let frame = 0;
        let again = false;
        const run = () => {
            frame = 0;
            apply();
            if (!again) return;
            again = false;
            frame = requestAnimationFrame(run);
        };
        const onScroll = () => {
            if (frame) {
                again = true;
                return;
            }
            frame = requestAnimationFrame(run);
        };

        // Nothing to draw above md or for a reader who asked for less motion,
        // and nothing to listen for either: attached regardless, this wrote
        // six empty style properties per scroll event on every desktop
        // directory page.
        const listening = mobile.matches && !calm.matches;
        apply();
        // The page's own height is the other thing that moves the mark, and no
        // scroll event announces it. These pages grow by a header's height
        // while scrolled and shrink back at the top, a frame or more after the
        // scrolling stops — so the last run measured the grown layout and
        // pinned the mark 69px low, and nothing came along to correct it.
        // Watching `body` rather than `documentElement`, which does not report
        // the change.
        const grown = new ResizeObserver(onScroll);
        if (listening) {
            window.addEventListener('scroll', onScroll, { passive: true });
            window.addEventListener('resize', onScroll);
            grown.observe(document.body);
        }
        mobile.addEventListener('change', onChange);
        calm.addEventListener('change', onChange);
        return () => {
            grown.disconnect();
            if (frame) cancelAnimationFrame(frame);
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            mobile.removeEventListener('change', onChange);
            calm.removeEventListener('change', onChange);
        };
    }, [reattach]);

    return (
        <div ref={boxRef} aria-hidden="true" className="mb-6 h-14 md:hidden">
            <div
                ref={markRef}
                className="-z-10 h-full"
            >
                <Logomark className="pointer-events-none h-14 w-auto" />
            </div>
        </div>
    );
};
